import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, MessageCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Sale } from '@/pages/MinhasVendas';
import { saleService } from '@/services/sale.service';
import { toast } from 'sonner';

interface WarrantyModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSale: Sale | null;
  userData: any;
  onDownloadWarranty?: () => void; // Torna opcional pois vamos implementar internamente
}

export function WarrantyModal({
  isOpen,
  onClose,
  selectedSale,
  userData,
  onDownloadWarranty
}: WarrantyModalProps) {
   // console.log(selectedSale)
  if (!selectedSale) return null;

  const formatDate = (dateString: string): string => {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ptBR });
  };

  const formatTime = (dateString: string): string => {
    return format(new Date(dateString), 'HH:mm', { locale: ptBR });
  };

  // Usa dados reais da warranty se disponíveis, senão usa mock
  const warrantyData = selectedSale.warranty ? {
    client: selectedSale.warranty.client,
    data_compra: selectedSale.warranty.data_compra,
    codigos: selectedSale.warranty.codigos,
    url_download: selectedSale.warranty.url_download,
    idwarranty: selectedSale.warranty.idwarranty
  } : {
    client: {
      nome_cliente: selectedSale.nome_cliente || 'Cliente não informado',
      telefone_cliente: '(11) 99999-9999', // Mock data
      cpf_cliente: '000.000.000-00' // Mock data
    },
    data_compra: selectedSale.date_created,
    codigos: '1234567890123 (anel), 9876543210987 (brinco)', // Mock data
    url_download: null, // Mock - seria vindo da API
    idwarranty: selectedSale.warranty_id
  };

  /**
   * Implementação equivalente ao downloadGarantia do Angular
   * @param shareWapp Se deve compartilhar no WhatsApp
   */
  const downloadGarantia = async (shareWapp: boolean = false) => {
    toast.info('Baixando...');
    
    const fileName = `Garantia_XDINF_${selectedSale.codepagarme || warrantyData.idwarranty}.png`;
    
    // Se já existe URL de download salva
    if (warrantyData.url_download) {
      const downloadUrl = `${import.meta.env.VITE_API_URL}${warrantyData.url_download}`;
      
      if (shareWapp) {
        const whatsappUrl = `https://api.whatsapp.com/send?text=Olá! Segue o link para download da garantia de peças da sua compra no X do Infinito: ${downloadUrl}`;
        window.open(whatsappUrl, '_blank');
      } else {
        window.open(downloadUrl, '_blank');
      }
    } else {
      // Gerar imagem e fazer upload
      const elementoHTML = document.getElementById('warranty-content');
      
      if (!elementoHTML) {
        toast.error('Erro ao localizar conteúdo da garantia');
        return;
      }

      try {
        // Usando html2canvas para capturar o elemento
        const html2canvas = (await import('html2canvas')).default;
        
        // Aplicar transformação de escala como no Angular
        const canvas = await html2canvas(elementoHTML, {
          useCORS: true,
          allowTaint: true,
          scale: 2,
          backgroundColor: '#e9e9e9'
        });
        
        const dataUrl = canvas.toDataURL('image/png');
        const warrantyId = warrantyData.idwarranty || selectedSale.warranty_id;
        
        // Upload da imagem
        const response = await saleService.uploadWarrantyImage(warrantyId, dataUrl, fileName);
        
        if (response && response.urlDownload) {
          const downloadUrl = `${import.meta.env.VITE_API_URL}${response.urlDownload}`;
          
          if (shareWapp) {
            const whatsappUrl = `https://api.whatsapp.com/send?text=Olá! Segue o link para download da garantia de peças da sua compra no X do Infinito: ${downloadUrl}`;
            window.open(whatsappUrl, '_blank');
          } else {
            window.open(downloadUrl, '_blank');
          }
        } else {
          toast.error('Erro ao processar upload da garantia');
        }
        
      } catch (error) {
        console.error('Erro ao gerar garantia:', error);
        toast.error('Erro ao gerar garantia');
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-[#e9e9e9] overflow-hidden p-4">
        <DialogHeader className="sr-only">
          <DialogTitle>Certificado de Garantia</DialogTitle>
        </DialogHeader>
        
        {/* Action Buttons */}
        <div className="absolute top-4 left-4 right-4 z-20 flex justify-between">
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => downloadGarantia(false)}
              className="rounded-full h-10 w-10 p-0 shadow-lg"
            >
              <Download className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              className="rounded-full h-10 w-10 p-0 bg-green-600 hover:bg-green-700 shadow-lg"
              onClick={() => downloadGarantia(true)}
            >
              <MessageCircle className="h-4 w-4" />
            </Button>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="rounded-full h-10 w-10 p-0 shadow-lg"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div 
          id="warranty-content" 
          className="exportImage p-6 mx-auto max-w-md text-black bg-[#e9e9e9] border-2 border-gray-400/20 rounded-sm"
        >
            <div className="text-center mb-2">
              <img 
                src="/v2/logos/logo-horizontal.png" 
                alt="Logo" 
                style={{ width: '160px' }} 
                className="mx-auto"
              />
            </div>
            <hr className="my-2 border-gray-400" />
            <h2 className="text-center text-lg font-bold mb-2 text-black">
              CERTIFICADO DE GARANTIA
            </h2>
            <hr className="my-2 border-gray-400" />
            
            <div className="space-y-2 mb-2">
              <div className="flex items-center justify-between">
                {selectedSale.id && (
                  <div>
                    <p className="m-0 font-bold text-[0.65rem] text-black">Código da Compra:</p>
                    <p className="m-0 text-[0.65rem] text-black">
                      #{selectedSale.codepagarme || `XDINF${selectedSale.id}`}
                    </p>
                  </div>
                )}
                {selectedSale.id && (
                  <div>
                    <p className="m-0 font-bold text-[0.65rem] text-black">Data da Compra:</p>
                    <p className="m-0 text-[0.65rem] text-black">{formatDate(warrantyData.data_compra)}</p>
                  </div>
                )}
                {selectedSale.id && (
                  <div className="text-right">
                    <p className="m-0 font-bold text-[0.65rem] text-black">Hora:</p>
                    <p className="m-0 text-[0.65rem] text-black">{formatTime(warrantyData.data_compra)}</p>
                  </div>
                )}
              </div>
            </div>
            
            <hr className="my-2 border-gray-400" />
            
            <div className="flex items-center justify-between mb-2">
                <div>
                    <p className="m-0 font-bold text-[0.65rem] text-black">Revendedor(a):</p>
                    <p className="m-0 text-[0.65rem] text-black">
                    #{selectedSale.userClient?.codigo || 'N/A'} {userData?.nome_razao_social || 'N/A'}
                    </p>
                </div>
                <div>
                    <p className="m-0 font-bold text-[0.65rem]">Cliente:</p>
                    <p className="m-0 text-[0.65rem]">{warrantyData.client.nome_cliente}</p>
                </div>
              {!selectedSale.id && (
                <div>
                  <p className="m-0 font-bold text-[0.65rem] text-black">Data da Compra:</p>
                  <p className="m-0 text-[0.65rem] text-black">{formatDate(warrantyData.data_compra)}</p>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between mb-2 gap-4">
              <div>
                <div className="mt-2">
                  <p className="m-0 font-bold text-[0.65rem]">Telefone Cliente:</p>
                  <p className="m-0 text-[0.65rem]">{warrantyData.client.telefone_cliente}</p>
                </div>
              </div>
              
              <div>
                <p className="m-0 font-bold text-[0.65rem]">Documento Cliente:</p>
                <p className="m-0 text-[0.65rem]">{warrantyData.client.cpf_cliente}</p>
              </div>

            </div>
            <hr className="my-2 border-gray-400" />
            
            <div className="mb-2">
              <p className="m-0 font-bold text-[0.65rem]">Peças:</p>
              <ul className="m-0 p-0 pl-4 text-[0.65rem] list-disc">
                {warrantyData.codigos.split(', ').map((peca, index) => (
                  <li key={index}>{peca}</li>
                ))}
              </ul>
            </div>
            
            <hr className="my-2 border-gray-400 md:hidden" />
            
            <div className="mb-2 text-[0.50rem]">
              <div className='text-[0.50rem]'>
                <p className="m-0 text-center font-bold">GARANTIA DE 6 MESES</p>
                <p className="m-0 mt-2">
                  Nossa garantia responsabiliza-se por qualquer defeito de fabricação, porém ficará anulada caso sejam constatadas as seguintes irregularidades devido ao mau uso:
                </p>
                <ul className="m-0 p-0 pl-4 mt-2 list-disc">
                  <li>Quebra ou deformação da peça por impacto;</li>
                  <li>Desgaste natural pelo uso;</li>
                  <li>Escurecimento ou alteração da cor devido a contato com produtos químicos, perfumes ou cosméticos;</li>
                  <li>Perda de pedras, cristais ou pérolas por mau uso;</li>
                  <li>Peças cravejadas com pedras escurecidas devido ao mau uso;</li>
                  <li>Oxidação decorrente de contato com água, suor ou umidade excessiva.</li>
                </ul>
                <p className="m-0 mt-2">
                  <strong>Obs.:</strong> Peças enviadas para garantia passarão por uma avaliação técnica criteriosa. Se for constatado que o dano ocorreu devido a mau uso ou desgaste natural, a garantia não será aplicada e a peça será devolvida sem reparos.<br />
                  Após avaliação e constatado defeito de fabricação, a peça será enviada à fábrica para os devidos reparos e tem um prazo de até 60 dias para retorno.
                </p>
              </div>
            </div>
            
            <hr className="my-2 border-gray-400" />
            <div className="text-center">
              <p className="text-[0.65rem] mb-1">X do Infinito Comércio Eletrônico Ltda ©</p>
              <p className="text-[0.65rem]">16.869.140/0001-30</p>
            </div>
          </div>
      </DialogContent>
    </Dialog>
  );
}